import React, { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import './index.css';

/* ======================== CONFIG ======================== */
const PLAYLIST = [
  '/videos/001_master.m3u8',
  '/videos/002_master.m3u8',
  '/videos/003_master.m3u8',
  '/videos/004_master.m3u8',
  '/videos/005_master.m3u8',
];

// ✅ Anchor: ศุกร์ 6 ก.ย. 2025 20:00 (+07) = 13:00 UTC
const ANCHOR_UTC_MS = Date.UTC(2025, 8, 5, 13, 0, 0); 
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/* ====================== TIME HELPERS ===================== */
function useServerClockSkew() {
  const skewRef = useRef(0);

  const fetchServerNow = useCallback(async () => {
    try {
      const res = await fetch('/', { method: 'HEAD', cache: 'no-store' });
      const dateStr = res.headers.get('date');
      if (dateStr) {
        const serverMs = new Date(dateStr).getTime();
        skewRef.current = serverMs - Date.now();
      }
    } catch {
      skewRef.current = 0;
    }
  }, []);

  const serverNow = useCallback(() => Date.now() + skewRef.current, []);

  return { skewRef, fetchServerNow, serverNow };
}

// ✅ คำนวณ index ปัจจุบัน
function getWeeklyIndex(nowUtcMs, length = PLAYLIST.length) {
  if (nowUtcMs < ANCHOR_UTC_MS) return { idx: 0, weeks: -1 };

  const passedRounds = Math.floor((nowUtcMs - ANCHOR_UTC_MS) / WEEK_MS);
  const idx = ((passedRounds % length) + length) % length;
  return { idx, weeks: passedRounds };
}

// ✅ คำนวณเวลาศุกร์ถัดไป 20:00 (+07)
function getNextChangeUtcMs(nowUtcMs) {
  if (nowUtcMs < ANCHOR_UTC_MS) return ANCHOR_UTC_MS;
  const passedRounds = Math.floor((nowUtcMs - ANCHOR_UTC_MS) / WEEK_MS) + 1;
  return ANCHOR_UTC_MS + passedRounds * WEEK_MS;
}

function bangkokDateStr(utcMs) {
  const dt = new Date(utcMs + 7 * 3600 * 1000);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/* ========================= APP ========================== */
export default function App() {
  const videoRef       = useRef(null);
  const hlsRef         = useRef(null);
  const switchTimerRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const [msg, setMsg] = useState('');

  const { fetchServerNow, serverNow } = useServerClockSkew();

  /* ---------- เริ่มเล่น ---------- */
  const handleStart = useCallback(async () => {
    setMsg('');
    await fetchServerNow();
    const now = serverNow();
    const todayTH = bangkokDateStr(now);

    const { idx: weekIdx, weeks } = getWeeklyIndex(now);
    console.log(`[DEBUG] handleStart: now=${new Date(now).toISOString()}, weeks=${weeks}, idx=${weekIdx}`);

    // เคลียร์ sessionStorage ถ้าเป็นสัปดาห์ใหม่
    const lastPlayedWeek = sessionStorage.getItem('playedWeek');
    if (lastPlayedWeek !== String(weeks)) {
      console.log('[DEBUG] New week detected -> clearing playedDate');
      sessionStorage.removeItem('playedDate');
      sessionStorage.setItem('playedWeek', String(weeks));
    }

    const playedDate = sessionStorage.getItem('playedDate');
    if (playedDate === todayTH) {
      setMsg('กรุณาเปิดเบราว์เซอร์ใหม่ หรือรอวันถัดไปเพื่อรับชมอีกครั้ง');
      return;
    }

    setIdx(weekIdx);
    setPlaying(true);

    const el = document.documentElement;
    (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el);
  }, [fetchServerNow, serverNow]);

  /* ---------- ตั้งนาฬิกาสลับรอบทุกศุกร์ 20:00 ---------- */
  useEffect(() => {
    if (!playing) return;

    const schedule = async () => {
      await fetchServerNow();
      const now = serverNow();
      const nextChange = getNextChangeUtcMs(now);
      const delay = Math.max(0, nextChange - now);

      console.log(`[DEBUG] schedule next change at ${new Date(nextChange).toISOString()}, delay=${delay}ms`);

      switchTimerRef.current = setTimeout(async () => {
        await fetchServerNow();
        const { idx: newIdx, weeks } = getWeeklyIndex(serverNow());
        console.log(`[DEBUG] Weekly change triggered -> newIdx=${newIdx}, weeks=${weeks}`);
        sessionStorage.removeItem('playedDate');
        setIdx(newIdx);
        schedule();
      }, delay);
    };

    schedule();
    return () => clearTimeout(switchTimerRef.current);
  }, [playing, fetchServerNow, serverNow]);

  /* ---------- ตรวจสอบเมื่อแท็บกลับมา active ---------- */
  useEffect(() => {
    const onVis = async () => {
      if (document.visibilityState === 'visible' && playing) {
        await fetchServerNow();
        const { idx: newIdx, weeks } = getWeeklyIndex(serverNow());
        console.log(`[DEBUG] visibilitychange -> idx=${newIdx}, weeks=${weeks}`);
        setIdx((cur) => (cur !== newIdx ? newIdx : cur));
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [playing, fetchServerNow, serverNow]);

  /* ---------- Hls instance ---------- */
  useEffect(() => {
    if (!playing) return;
    if (hlsRef.current || !Hls.isSupported()) return;

    const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
    const video = videoRef.current;
    hlsRef.current = hls;

    hls.attachMedia(video);

    const onEnded = async () => {
      await fetchServerNow();
      const todayTH = bangkokDateStr(serverNow());
      console.log(`[DEBUG] Video ended -> set playedDate=${todayTH}`);
      sessionStorage.setItem('playedDate', todayTH);

      if (document.fullscreenElement) {
        try { await document.exitFullscreen(); } catch {}
      }

      setPlaying(false);
      setMsg('ขอบคุณครับ');
    };
    video.addEventListener('ended', onEnded);

    let recovered = false;
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered) {
        recovered = true;
        hls.recoverMediaError();
      }
    });

    return () => {
      video.removeEventListener('ended', onEnded);
      hls.destroy();
      hlsRef.current = null;
    };
  }, [playing, fetchServerNow, serverNow]);

  /* ---------- เปลี่ยน source เมื่อ idx เปลี่ยน ---------- */
  useEffect(() => {
    if (!playing) return;

    const video = videoRef.current;
    const src = PLAYLIST[idx];
    console.log(`[DEBUG] Switching to idx=${idx}, src=${src}`);

    if (!Hls.isSupported()) {
      video.loop = false;
      video.src = src;
      video.currentTime = 0;
      video.play().catch(console.warn);
      return;
    }

    const hls = hlsRef.current;
    if (!hls) return;

    hls.stopLoad();
    hls.loadSource(src);

    const onParsed = () => {
      hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
      video.loop = false;
      video.currentTime = 0;
      video.play().catch(console.warn);
    };
    hls.on(Hls.Events.MANIFEST_PARSED, onParsed);
  }, [idx, playing]);

  /* ========================= UI ========================== */
  return (
    <div className={`app ${playing ? 'video-mode' : 'landing-mode'}`}>
      {!playing ? (
        <>
          <h1 className="landing-title">ประชาสัมพันธ์ 4Rs</h1>
          {msg && <p style={{ color:'#ffcc00', fontWeight:600 }}>{msg}</p>}
          {Array.from({ length: 12 }).map((_, i) => <br key={i} />)}
          <button className="start-btn" onClick={handleStart}>
            🚀 เริ่มเล่นวิดีโอ
          </button>
        </>
      ) : (
        <video
          ref={videoRef}
          className="video-player"
          controls={false}
          playsInline
          style={{ width: '100%', height: '100%', background: '#000' }}
        />
      )}
    </div>
  );
}
